import { Alert } from '@mantine/core';
import { useTranslation } from 'react-i18next';

import {
  TravelApprovalStep,
  TravelRequest,
  travelBoardReached,
  travelStepFamily,
  travelStepRoleLabel,
  travelStepTitle,
} from '../model/TravelRequestTypes';
import { travelProcessNextKey } from './TravelProcessTimeline';

interface TravelRequestNextAlertProps {
  request: TravelRequest;
  isRequester: boolean;
  returnDocumentsComplete: boolean;
}

const alertColor: Record<string, string> = { stopped: 'red', done: 'green' };

function pendingStepOf(steps: TravelApprovalStep[]) {
  return steps.filter(step => step.decision === 'PENDING').sort((a, b) => a.orderIndex - b.orderIndex)[0];
}

export function TravelRequestNextAlert({ request, isRequester, returnDocumentsComplete }: TravelRequestNextAlertProps) {
  const { t } = useTranslation();
  const key = travelProcessNextKey(
    request.status,
    request.files.length > 0,
    returnDocumentsComplete,
    travelBoardReached(request),
  );
  const pendingStep =
    key === 'approval' || key === 'administrativeBoard' ? pendingStepOf(request.approvalSteps) : undefined;

  if (pendingStep) {
    const step = travelStepTitle(pendingStep, t);
    const roleLabel = travelStepRoleLabel(pendingStep, t);
    // The HEAD and verification step labels already name the role that holds them, and so does an overridden one.
    const roleNamedByStep =
      pendingStep.stepKey === 'HEAD' || pendingStep.stepKey === 'SECRETARIAT_VERIFICATION' || roleLabel === step;
    const approver = pendingStep.approver?.name ?? (roleNamedByStep ? null : roleLabel);
    const family = travelStepFamily(pendingStep);
    return (
      <Alert
        color="blue"
        title={t('travel.requests.view.waitingTitle')}
      >
        {approver == null
          ? t(`travel.stepFamily.${family}.waitingFor`, { step })
          : t(`travel.stepFamily.${family}.waitingForApprover`, { step, approver })}
      </Alert>
    );
  }

  if (!isRequester) {
    // Only the requester's download approves, so any other reader would otherwise see an issued order that never moves.
    return key === 'travelOrder' ? (
      <Alert
        color="blue"
        title={t('travel.requests.view.waitingTitle')}
      >
        {t('travel.requests.process.next.travelOrderReader')}
      </Alert>
    ) : null;
  }

  return (
    <Alert
      color={alertColor[key] ?? 'blue'}
      title={t('travel.requests.view.nextTitle')}
    >
      {t(`travel.requests.process.next.${key}`)}
    </Alert>
  );
}
